import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {colors} from 'shells/colors/colors';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {FormField} from 'shells/form/form-field';
import {SelectedFieldCard} from 'shells/form/sidebar-fields/selected-field-card';
import {LoadingPage} from 'shells/loading-page/loading-page';

import {useUpdateCompetitorIqWebsites} from './graphql/hooks/use-update-competitor-iq-websites';
import {TextfieldWithSave} from './textfield-with-save';

type Error = {
    competitor: string;
    message: string;
};

type Props = {
    competitors: string[];
    isLoading?: boolean;
    isInteractive?: boolean;
    isDisabled?: boolean;
};

const MAX_COMPETITORS = 5;

export function CompetitorsList(props: Props) {
    const [errors, setErrors] = React.useState<Error[]>([]);
    const [duplicateError, setDuplicateError] = React.useState<boolean>(false);
    const [competitors, setCompetitors] = React.useState<string[]>(props.competitors);
    const [newCompetitor, setNewCompetitor] = React.useState<string>('');
    const [isEditingCompetitor, setIsEditingCompetitor] = React.useState<string | null | undefined>(
        null
    );
    const [editedCompetitor, setEditedCompetitor] = React.useState<string>('');
    const [isSavingAdded, setIsSavingAdded] = React.useState<boolean>(false);
    const [isSavingEdited, setIsSavingEdited] = React.useState<boolean>(false);

    const {updateWebsiteSettings} = useUpdateCompetitorIqWebsites();

    const maxReachedText = props.isInteractive ? (
        <div className='light-text' style={{marginLeft: '8px'}}>
            You’ve reached the maximum of five competitors
        </div>
    ) : undefined;

    if (props.isLoading) {
        return <LoadingPage />;
    }

    const onSaveCompetitors = (updatedCompetitors: string[], addedCompetitor?: string) => {
        return updateWebsiteSettings({competitors: updatedCompetitors}).catch((e) => {
            const message = safelyGetGraphQLErrorMessage(e);

            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            addedCompetitor && message
                ? setErrors(errors.concat({competitor: addedCompetitor, message}))
                : undefined;
        });
    };

    const onAddCompetitor = () => {
        if (competitors.includes(newCompetitor)) {
            setDuplicateError(true);
        } else {
            setIsSavingAdded(true);

            const updatedCompetitors = competitors.concat(newCompetitor);
            onSaveCompetitors(updatedCompetitors, newCompetitor).then(() => {
                setCompetitors(updatedCompetitors);
                setIsSavingAdded(false);
                setNewCompetitor('');
            });
        }
    };

    const onRemoveCompetitor = (competitorToRemove: string) => {
        const updatedCompetitors = competitors.filter(
            (competitor) => competitor !== competitorToRemove
        );
        setErrors(errors.filter((error) => error.competitor !== competitorToRemove));
        setCompetitors(updatedCompetitors);
        onSaveCompetitors(updatedCompetitors);
    };

    const onSaveEditedCompetitor = () => {
        setIsSavingEdited(true);
        const updatedCompetitors = competitors.map((competitor) =>
            competitor === isEditingCompetitor ? editedCompetitor : competitor
        );
        setErrors(errors.filter((error) => error.competitor !== isEditingCompetitor));

        onSaveCompetitors(updatedCompetitors, editedCompetitor).then(() => {
            setCompetitors(updatedCompetitors);
            setIsSavingEdited(false);
            setIsEditingCompetitor(null);
            setEditedCompetitor('');
        });
    };

    return (
        <div>
            {!props.isInteractive && competitors.length === 0 ? (
                <i>No competitors added</i>
            ) : undefined}
            {competitors.map((competitor) => {
                const competitorError = errors.find((error) => error.competitor === competitor);
                const errorMessage = competitorError ? competitorError.message : undefined;

                if (isEditingCompetitor === competitor) {
                    return (
                        <TextfieldWithSave
                            key={competitor}
                            value={editedCompetitor}
                            onChange={setEditedCompetitor}
                            focusOnMount={true}
                            onSave={onSaveEditedCompetitor}
                            isSaving={isSavingEdited}
                            // @ts-expect-error
                            disabled={!editedCompetitor}
                            errorSaving={Boolean(errorMessage)}
                        />
                    );
                }

                return (
                    <FormField
                        key={competitor}
                        innerId={competitor}
                        errorMessage={errorMessage}
                        noMargin={!errorMessage}
                    >
                        <SelectedFieldCard
                            name={competitor}
                            borderColor={errorMessage ? colors.rose : colors.blue}
                            onRemove={
                                props.isInteractive
                                    ? () => onRemoveCompetitor(competitor)
                                    : undefined
                            }
                            onClick={
                                props.isInteractive
                                    ? () => {
                                          setIsEditingCompetitor(competitor);
                                          setEditedCompetitor(competitor);
                                      }
                                    : undefined
                            }
                            isDisabled={props.isDisabled}
                        />
                    </FormField>
                );
            })}
            {competitors.length < MAX_COMPETITORS ? (
                <TextfieldWithSave
                    value={newCompetitor}
                    onChange={setNewCompetitor}
                    onSave={onAddCompetitor}
                    isSaving={isSavingAdded}
                    disableButton={!newCompetitor}
                    disableTextField={props.isDisabled}
                    // @ts-expect-error
                    errorSaving={errors.length > 0}
                    isAddButton={true}
                />
            ) : (
                maxReachedText
            )}
            {duplicateError ? (
                <FlashBanner
                    hasBorder={true}
                    type='warning'
                    onHidden={() => setDuplicateError(false)}
                >
                    This competitor was added already
                </FlashBanner>
            ) : null}
        </div>
    );
}
